import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useLocation,
} from '@tanstack/react-router'
import { WorkspaceDTO } from '@deepguide-ai/types'
import { useAtom } from 'jotai'
import { toast } from 'sonner'
import {
  activeWorkspaceWithStorageAtom,
  Workspace,
  usePeriodicWorkspaceRefresh,
} from '@/stores/workspaceStore'
import { activeOrganizationWithStorageAtom } from '@/stores/workspaceStore'
import {  trpc, trpcClient } from '@/lib/trpc'
// TopicProvider no longer needed - using jotai atoms directly
import { SearchProvider } from '@/context/search-context'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useTopicHierarchyQuery } from '@/hooks/useTopicHierarchyQuery'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import PageLoading from '@/components/PageLoading'
import { ActivationPage } from '@/components/screenata/assess/ActivationPage'
import { useActivationState } from '@/components/screenata/assess/use-activation-state'
import { DelegationContext } from '@/components/partner-delegation-banner'
import NotFoundError from '@/pages/errors/not-found-error'
import { AuditPackageDownloadProvider } from '@/components/auditor-center/AuditPackageDownloadProvider'



export const Route = createFileRoute('/(protected)/workspaces/$workspaceId')({
  component: WorkspaceLayout,
  notFoundComponent: NotFoundError,
  beforeLoad: async ({ params }) => {
    // Ensure we have a valid workspace ID
    if (!params.workspaceId) {
      throw redirect({
        to: `/workspaces`,
      })
    }
  },
})

function WorkspaceLayout() {
  const navigate = useNavigate()
  const [delegation, setDelegation] = useState<DelegationContext | null>(null);
 
  const { refetch: refetchWorkspaces } = usePeriodicWorkspaceRefresh() // Add periodic refresh for workspace data

  // All hooks must be called at the top level
  const { workspaceId: workspaceSlug } = Route.useParams()
  const [activeOrganization, setActiveOrganization] = useAtom(
    activeOrganizationWithStorageAtom
  )
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false)
  const [, setActiveWorkspace] = useAtom(activeWorkspaceWithStorageAtom)


  // Calculate workspace ID
  let workspaceId = activeOrganization?.workspaces?.find(
    (workspace: WorkspaceDTO) =>
      workspace.slug === workspaceSlug || workspace.id === workspaceSlug
  )?.id

  // Re-check workspace ID after potential refresh
  if (!workspaceId && activeOrganization) {
    workspaceId = activeOrganization.workspaces?.find(
      (workspace: WorkspaceDTO) =>
        workspace.slug === workspaceSlug || workspace.id === workspaceSlug
    )?.id
  }

  // All useQuery hooks must be called unconditionally
  const { data: workspace, isLoading: isWorkspaceLoading } = useQuery({
    ...trpc.workspaces.getWorkspaceById.queryOptions({
      id: workspaceId!,
    }),
    enabled: !!workspaceId && typeof workspaceId === 'string',
  })

  const isScreenataWorkspace = workspace?.type === 'SCREENATA'

  // RFC-236 r3.7: an unactivated assessment organization sees the activation
  // page instead of the workspace, whatever URL it landed on.
  const { data: activation, isLoading: isActivationLoading } = useActivationState(
    !!workspaceId && typeof workspaceId === 'string',
  )

  const { isAuditor, isEmployee } = useWorkspacePermissions(workspaceId)
  const location = useLocation()

  // Redirect restricted roles to their dedicated centers if they navigate elsewhere
  useEffect(() => {
    const isAnonymousReportDetail = /^\/workspaces\/[^/]+\/anonymous-reports\/[^/]+\/?$/.test(location.pathname)
    if (isAuditor && workspaceSlug && !isAnonymousReportDetail && !location.pathname.includes('/auditor-center')) {
      navigate({
        to: '/auditor-center',
        replace: true,
      })
      return
    }
    if (isEmployee && workspaceSlug && !isAnonymousReportDetail && !location.pathname.includes('/employee-center')) {
      navigate({
        to: '/workspaces/$workspaceId/employee-center',
        params: { workspaceId: workspaceSlug },
        replace: true,
      })
    }
  }, [isAuditor, isEmployee, workspaceSlug, location.pathname, navigate])


  // Force refresh organization data when workspace is not found
  const forceRefreshOrganization = async () => {
    try {
      console.log('Force refreshing organization data...')

      // Use the refetch function from the periodic refresh hook if available
      if (refetchWorkspaces) {
        console.log('Using TanStack Query refetch...')
        const result = await refetchWorkspaces()
        console.log(
          'Refetch result:',
          result.data?.organization?.workspaces?.length,
          'workspaces'
        )
        if (result.data?.organization) {
          return result.data.organization
        }
      }

      // Fallback to direct API call with cache busting
      console.log('Using direct API call as fallback...')
      const { organization } =
        await trpcClient.organizations.getOrganizationWithWorkspaces.query({
          // Add a timestamp to bust any potential caching
          _timestamp: Date.now(),
        } as any)

      if (organization) {
        console.log(
          'Direct API call returned',
          organization.workspaces?.length,
          'workspaces'
        )
        localStorage.setItem('auth:activeOrg', JSON.stringify(organization))
        setActiveOrganization(organization)
        console.log('Organization data force refreshed successfully')
        return organization
      }
    } catch (error) {
      console.error('Failed to force refresh organization data:', error)
      return null
    }
  }

  useEffect(() => {
    if (!workspaceId && !hasTriedRefresh && workspaceSlug) {
      console.log(
        'Workspace not found, attempting to refresh organization data...'
      )

      // Add a small delay to ensure any pending operations complete
      const refreshWithDelay = async () => {
        await new Promise((resolve) => setTimeout(resolve, 100))
        await forceRefreshOrganization()

        // Force a re-render after refresh by updating a dummy state
        setTimeout(() => {
          setHasTriedRefresh(true)
        }, 200)
      }

      refreshWithDelay()
    }
  }, [workspaceId, hasTriedRefresh, workspaceSlug])

  useEffect(() => {
    const currentWorkspaceSlug = location.pathname.match(/\/workspaces\/([^/]+)/)?.[1]

    if (
      workspace &&
      currentWorkspaceSlug &&
      (workspace.id === currentWorkspaceSlug ||
        workspace.slug === currentWorkspaceSlug)
    ) {
      console.log(
        '🔄 Setting active workspace in route:',
        workspace.id,
        workspace.title
      )
      setActiveWorkspace(workspace as unknown as Workspace)
    }
  }, [workspace, location.pathname, setActiveWorkspace])

  useEffect(() => {
    const checkDelegation = () => {
      try {
        const stored = localStorage.getItem('partner:delegation');
        if (stored) {
          const parsed = JSON.parse(stored) as DelegationContext;

          // Check if delegation is still valid (within 24 hours)
          const age = Date.now() - parsed.timestamp;
          if (age < 24 * 60 * 60 * 1000) {
            setDelegation(parsed);
          } else {
            // Clear expired delegation
            localStorage.removeItem('partner:delegation');
          }
        }
      } catch (error) {
        console.error('Failed to check delegation context:', error);
      }
    };

    checkDelegation();

    // Re-check on storage changes
    window.addEventListener('storage', checkDelegation);
    return () => window.removeEventListener('storage', checkDelegation);
  }, []);


  // Wait until the workspace type is known before deciding — otherwise the
  // query fires during the initial load window (workspace === undefined) and
  // would hit the legacy docs endpoint even on Screenata workspaces.
  useTopicHierarchyQuery({ enabled: !isWorkspaceLoading && !isScreenataWorkspace })

  // Handle workspace not found after refresh attempt
  useEffect(() => {
    if (!workspaceId && hasTriedRefresh) {
      const availableWorkspaces =
        activeOrganization?.workspaces?.map((w) => ({
          id: w.id,
          slug: w.slug,
          title: w.title,
        })) || []
      console.error('Workspace not found after refresh:', {
        workspaceSlug,
        availableWorkspaces,
        totalWorkspaces: availableWorkspaces.length,
      })

      // Check if there's a workspace that might match by title or partial slug
      const possibleMatches = availableWorkspaces.filter(
        (w) =>
          w.slug.includes(workspaceSlug) ||
          w.title.toLowerCase().includes(workspaceSlug.toLowerCase()) ||
          workspaceSlug.includes(w.slug)
      )

      if (possibleMatches.length > 0) {
        console.log('Possible workspace matches found:', possibleMatches)
        const bestMatch = possibleMatches[0]
        console.log('Attempting to redirect to best match:', bestMatch)
        navigate({
          to: `/workspaces/${bestMatch.slug}`,
          replace: true,
        })
        return
      }

      toast.error(`Workspace "${workspaceSlug}" not found`)
      navigate({
        to: `/workspaces`,
      })
    }
  }, [workspaceId, hasTriedRefresh, workspaceSlug, activeOrganization, navigate])

  if (!workspaceId && hasTriedRefresh) {
    return <PageLoading fullScreen />
  }

  // Show loading while we haven't tried refresh yet or while workspace is being looked up
  if (!workspaceId) {
    return <PageLoading fullScreen />
  }

  if (isWorkspaceLoading || isActivationLoading) {
    return (
      <div>
        <PageLoading fullScreen />
      </div>
    )
  }

  if (activation?.needsActivation) {
    return <ActivationPage state={activation} />
  }

  return (
    <SearchProvider>
      <AuditPackageDownloadProvider key={workspaceId}>
        <Outlet />
      </AuditPackageDownloadProvider>
    </SearchProvider>
  )
}
